import { Link, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { SiteNav, SiteNavSpacer } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";

interface Props {
  title: string;
  updated: string;
  lede?: string;
  description?: string;
  pageTitle?: string;
  canonicalPath?: string;
  children: React.ReactNode;
}

type Section = { id: string; label: string };

const PALETTE = {
  bg: "#F8F4EC",
  ink: "#1a1a1a",
  muted: "#6b6357",
  amber: "#C4823A",
  line: "rgba(0,0,0,0.08)",
  card: "#FFFFFF",
};

function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/^\d+\.\s*/, "")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export default function LegalShell({ title, updated, lede, description, pageTitle, canonicalPath, children }: Props) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [active, setActive] = useState<string>("");
  const { hash, pathname } = useLocation();
  const fullTitle = pageTitle ?? `${title} | HireEva`;
  const metaDescription = description ?? `${title} for HireEva — how we operate, what you agree to, and how to reach us.`;
  const canonicalUrl = `https://www.hireeva.io${canonicalPath ?? pathname}`;

  // Walk DOM after mount: assign ids to h2, build TOC.
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    const headings = Array.from(root.querySelectorAll("h2"));
    const next: Section[] = headings.map((h) => {
      const raw = h.textContent?.trim() ?? "";
      const id = h.id || slugify(raw);
      h.id = id;
      // Add a tiny anchor link (once)
      if (!h.querySelector("[data-anchor]")) {
        const a = document.createElement("a");
        a.href = `#${id}`;
        a.textContent = "#";
        a.setAttribute("data-anchor", "true");
        a.setAttribute("aria-label", `Link to ${raw}`);
        a.className = "legal-anchor";
        h.appendChild(a);
      }
      return { id, label: raw };
    });
    setSections(next);
  }, [children]);

  // Scroll to hash after sections mount
  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    const el = document.getElementById(id);
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [hash, sections.length]);

  // Active section observer
  useEffect(() => {
    if (!sections.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: 0 }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [sections]);

  return (
    <div style={{ minHeight: "100vh", background: PALETTE.bg, color: PALETTE.ink, fontFamily: "Barlow, sans-serif", lineHeight: 1.65 }}>
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <style>{`
        .legal-shell h2 {
          font-family: 'Instrument Serif', serif;
          font-style: italic;
          font-weight: 400;
          font-size: 28px;
          line-height: 1.15;
          margin: 56px 0 18px;
          color: ${PALETTE.ink};
          scroll-margin-top: 96px;
          position: relative;
        }
        .legal-shell h2:first-child { margin-top: 0; }
        .legal-shell h2 .legal-anchor {
          opacity: 0;
          margin-left: 10px;
          color: ${PALETTE.amber};
          text-decoration: none;
          font-family: 'JetBrains Mono', monospace;
          font-size: 18px;
          transition: opacity 160ms ease;
        }
        .legal-shell h2:hover .legal-anchor,
        .legal-shell h2 .legal-anchor:focus-visible { opacity: 1; }
        .legal-shell p { margin: 0 0 14px; }
        .legal-shell ul { padding-left: 0; margin: 0 0 16px; list-style: none; }
        .legal-shell ul li {
          position: relative;
          padding-left: 22px;
          margin-bottom: 8px;
        }
        .legal-shell ul li::before {
          content: "";
          position: absolute;
          left: 4px; top: 11px;
          width: 6px; height: 6px;
          background: ${PALETTE.amber};
          transform: rotate(45deg);
        }
        .legal-shell a { color: ${PALETTE.ink}; text-decoration: underline; text-underline-offset: 2px; text-decoration-color: ${PALETTE.amber}; }
        .legal-shell strong { font-weight: 600; }
        .legal-toc-link[data-active="true"] { color: ${PALETTE.ink}; background: rgba(196,130,58,0.08); border-color: rgba(196,130,58,0.35); }
        .legal-toc-link:hover { color: ${PALETTE.ink}; }
        @media (max-width: 1023px) {
          .legal-toc-desktop { display: none !important; }
          .legal-grid { grid-template-columns: minmax(0, 1fr) !important; }
        }
        @media (min-width: 1024px) {
          .legal-toc-mobile { display: none !important; }
        }
        @media print {
          .legal-noprint { display: none !important; }
          body { background: #fff !important; }
          .legal-shell h2 .legal-anchor { display: none; }
        }
      `}</style>

      <SiteNav product="software" callSource="legal_nav" />
      <SiteNavSpacer />

      <main className="legal-shell" style={{ maxWidth: 1200, margin: "0 auto", padding: "56px 24px 80px" }}>
        {/* Hero */}
        <div style={{ maxWidth: 820, marginBottom: 40 }}>
          <p style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.amber, marginBottom: 12, fontFamily: "'JetBrains Mono', monospace" }}>
            <span aria-hidden style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: PALETTE.amber, boxShadow: `0 0 8px ${PALETTE.amber}`, marginRight: 8, verticalAlign: "middle" }} />
            Updated {updated}
          </p>
          <h1 style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: "clamp(44px, 7vw, 72px)", lineHeight: 1.02, margin: "0 0 18px", fontWeight: 400 }}>{title}</h1>
          {lede && <p style={{ fontSize: 17, color: PALETTE.muted, margin: 0, maxWidth: 640 }}>{lede}</p>}
        </div>

        {/* Mobile TOC */}
        {sections.length > 2 && (
          <details
            className="legal-toc-mobile legal-noprint"
            style={{
              border: `1px solid ${PALETTE.line}`,
              background: PALETTE.card,
              borderRadius: 8,
              padding: "12px 16px",
              marginBottom: 32,
            }}
          >
            <summary style={{ cursor: "pointer", fontSize: 13, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em", textTransform: "uppercase", color: PALETTE.muted }}>
              Jump to section
            </summary>
            <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0", display: "grid", gap: 6 }}>
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} style={{ fontSize: 14, color: PALETTE.ink, textDecoration: "none" }}>{s.label}</a>
                </li>
              ))}
            </ul>
          </details>
        )}

        {/* Two-col grid */}
        <div className="legal-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 240px) minmax(0, 1fr)", gap: 56, alignItems: "start" }}>
          {/* Desktop TOC */}
          <aside className="legal-toc-desktop legal-noprint" style={{ position: "sticky", top: 96 }}>
            <p style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.muted, fontFamily: "'JetBrains Mono', monospace", margin: "0 0 12px" }}>
              On this page
            </p>
            <nav style={{ display: "grid", gap: 2 }}>
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="legal-toc-link"
                  data-active={active === s.id}
                  style={{
                    fontSize: 13,
                    color: PALETTE.muted,
                    textDecoration: "none",
                    padding: "7px 10px",
                    borderRadius: 6,
                    border: "1px solid transparent",
                    transition: "color 120ms ease, background 120ms ease, border-color 120ms ease",
                    lineHeight: 1.35,
                  }}
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <div ref={contentRef} style={{ maxWidth: 720, fontSize: 16, color: "#3a3530" }}>
            {children}
          </div>
        </div>

        {/* Updated stamp — the legal floor itself lives in the shared footer */}
        <div className="legal-noprint" style={{ marginTop: 72, paddingTop: 24, borderTop: `1px solid ${PALETTE.line}` }}>
          <p style={{ fontSize: 13, color: PALETTE.muted, margin: 0 }}>Last updated {updated}</p>
        </div>
      </main>
      <SiteFooter callSource="legal_footer" />
    </div>
  );
}
